// ============================================================================
// UNITS OF MEASURE (ADR-0058 slice 1) — common.tbl_uom, tenant-wide.
// The units items are stocked, bought and sold in. Seeded (system) units keep
// their code and cannot be deleted; pack units (Shrink, Bundle) are added here.
// The list is small and not paged server-side: the hook returns every unit
// matching the category / search filter.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useUoms } from 'hooks/api';
import { uomService } from 'api/services';
import { UOM_CATEGORIES } from 'api/types';
import type { UomDto, UomCategory, CreateUomRequest } from 'api/types';

type ViewMode = 'list' | 'grid';

const ICONS = IconSystem as unknown as Record<string, React.ComponentType<{ size?: number }>>;

interface ApiError { response?: { status?: number; data?: { message?: string } } }
const apiStatus = (err: unknown) => (err as ApiError | null)?.response?.status;
const apiMessage = (err: unknown, fallback: string) =>
  (err as ApiError | null)?.response?.data?.message || fallback;
const CONCURRENCY_MESSAGE =
  'This unit was changed by someone else since you opened it. Close the form and reopen it to get the latest version.';

const categoryLabel = (c: string) => c.charAt(0) + c.slice(1).toLowerCase();
const CATEGORY_OPTIONS: SelectOption[] = UOM_CATEGORIES.map(c => ({ value: c, label: categoryLabel(c) }));

interface FormData {
  uomCode: string;
  uomName: string;
  symbol: string;
  category: string;
  decimalPlaces: string;
  isoCode: string;
  sortOrder: string;
  description: string;
}

const EMPTY: FormData = {
  uomCode: '', uomName: '', symbol: '', category: 'COUNT', decimalPlaces: '0', isoCode: '', sortOrder: '100', description: '',
};

// ---------------------------------------------------------------------------
// FORM MODAL
// ---------------------------------------------------------------------------

interface UomFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: UomDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const UomFormModal: React.FC<UomFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const formRef = useRef(form); formRef.current = form;
  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const r = recordRef.current;
    const m = modeRef.current;
    const data: FormData = r && m !== 'create'
      ? {
          uomCode: m === 'copy' ? '' : r.uomCode,
          uomName: m === 'copy' ? `${r.uomName} (Copy)` : r.uomName,
          symbol: r.symbol || '',
          category: r.category || 'COUNT',
          decimalPlaces: String(r.decimalPlaces ?? 0),
          isoCode: m === 'copy' ? '' : (r.isoCode || ''),
          sortOrder: String(r.sortOrder ?? 100),
          description: r.description || '',
        }
      : { ...EMPTY };
    setForm(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(form) !== initialSnapshotRef.current;
  }, [form]);

  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(prev => {
      if (!prev[f]) return prev;
      const next = { ...prev }; delete next[f]; return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentMode = modeRef.current;
    const currentInstanceId = createInstanceIdRef.current || '1';
    const currentCode = recordRef.current?.uomCode;
    const savedForm = { ...formRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedForm) !== savedSnapshot : false;

    const modalId = currentMode === 'create'
      ? `${tabId}-form-modal-create-${currentInstanceId}`
      : `${tabId}-form-modal-${currentMode}-${currentCode}`;

    const title = currentMode === 'create'
      ? (savedForm.uomCode ? `Add - ${savedForm.uomCode} (${currentInstanceId})` : `Add New (${currentInstanceId})`)
      : currentMode === 'copy'
        ? `Copy - ${currentCode}`
        : `Edit - ${currentCode}`;

    minimize({
      id: modalId, title, moduleName, tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentInstanceId : undefined,
          currentMode !== 'create' ? currentCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const isEdit = mode === 'edit' && !!record;
  const codeLocked = isEdit && !!record?.isSystem;
  const categoryLocked = isEdit && (record?.usageCount ?? 0) > 0;

  const checkCode = useCallback(async (code: string): Promise<boolean> => {
    if (!code) return true;
    try {
      const excludeId = modeRef.current === 'edit' ? recordRef.current?.id : undefined;
      const ok = await uomService.isCodeAvailable(code, excludeId);
      if (!ok) setFieldErrors(p => ({ ...p, uomCode: 'This unit code is already in use.' }));
      return ok;
    } catch {
      return true; // the server enforces uniqueness on save
    }
  }, []);

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!form.uomCode.trim()) errors.uomCode = 'Code is required';
    if (!form.uomName.trim()) errors.uomName = 'Name is required';
    if (!form.category) errors.category = 'Category is required';
    const decimals = Number(form.decimalPlaces);
    if (form.decimalPlaces.trim() === '' || !Number.isInteger(decimals) || decimals < 0 || decimals > 6) {
      errors.decimalPlaces = 'Enter a whole number from 0 to 6';
    }
    const sort = Number(form.sortOrder);
    if (form.sortOrder.trim() === '' || !Number.isInteger(sort) || sort < 0) {
      errors.sortOrder = 'Enter a whole number (0 or more)';
    }
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    if (!codeLocked && !(await checkCode(form.uomCode.trim().toUpperCase()))) return;
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, codeLocked, checkCode]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const payload: CreateUomRequest = {
        uomCode: form.uomCode.trim().toUpperCase(),
        uomName: form.uomName.trim(),
        symbol: form.symbol.trim() || null,
        category: form.category as UomCategory,
        decimalPlaces: Number(form.decimalPlaces),
        isoCode: form.isoCode.trim().toUpperCase() || null,
        sortOrder: Number(form.sortOrder),
        description: form.description.trim() || null,
      };
      if (mode === 'edit' && record) {
        await uomService.update(record.id, { ...payload, rowVersion: record.rowVersion });
      } else {
        await uomService.create(payload);
      }
      setSaving(false);
      notificationService.toast.success(`Unit "${payload.uomName}" ${mode === 'edit' ? 'updated' : 'created'}.`);
      onSaved();
      onClose();
    } catch (err: unknown) {
      setSaving(false);
      if (apiStatus(err) === 409) {
        notificationService.toast.error(apiMessage(err, CONCURRENCY_MESSAGE));
        onSaved();
      } else {
        notificationService.toast.error(apiMessage(err, 'Could not save the unit.'));
      }
    }
  }, [form, mode, record, onSaved, onClose]);

  if (!isOpen) return null;

  const hintStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add' : mode === 'copy' ? 'Copy' : 'Edit'} Unit of Measure
          </h2>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="uomCode">
              <label className="form-label">Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('uomCode') ? ' form-field-error' : ''}`}
                value={form.uomCode}
                maxLength={30}
                placeholder="e.g. SHRINK"
                disabled={codeLocked}
                style={codeLocked ? { background: 'var(--bg-secondary)' } : undefined}
                onChange={e => { clearFieldError('uomCode'); setForm(p => ({ ...p, uomCode: e.target.value })); }}
                onBlur={e => {
                  const v = e.target.value.trim().toUpperCase();
                  setForm(p => ({ ...p, uomCode: v }));
                  if (!codeLocked) void checkCode(v);
                }}
              />
              {codeLocked && <div style={hintStyle}>System unit: the code is fixed.</div>}
              {getFieldError('uomCode') && <div className="form-error-message">{getFieldError('uomCode')}</div>}
            </div>

            <div className="form-group" data-field="uomName">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('uomName') ? ' form-field-error' : ''}`}
                value={form.uomName}
                maxLength={100}
                placeholder="e.g. Shrink pack"
                onChange={e => { clearFieldError('uomName'); setForm(p => ({ ...p, uomName: e.target.value })); }}
              />
              {getFieldError('uomName') && <div className="form-error-message">{getFieldError('uomName')}</div>}
            </div>

            <div className="form-group" data-field="symbol">
              <label className="form-label">Symbol</label>
              <input
                type="text"
                className="form-input"
                value={form.symbol}
                maxLength={20}
                placeholder="e.g. shr"
                onChange={e => setForm(p => ({ ...p, symbol: e.target.value }))}
              />
            </div>

            <div className="form-group" data-field="category">
              <SyutSelect
                label={<>Category <span className="text-danger">*</span></>}
                options={CATEGORY_OPTIONS}
                value={form.category}
                onChange={v => { clearFieldError('category'); setForm(p => ({ ...p, category: (v as string) || '' })); }}
                isClearable={false}
                isDisabled={categoryLocked}
                error={getFieldError('category')}
                helperText={categoryLocked ? 'Units in use by items keep their category' : undefined}
                menuPortalTarget={document.body}
              />
            </div>

            <div className="form-group" data-field="decimalPlaces">
              <label className="form-label">Decimal Places</label>
              <input
                type="number"
                min={0}
                max={6}
                step={1}
                className={`form-input${getFieldError('decimalPlaces') ? ' form-field-error' : ''}`}
                value={form.decimalPlaces}
                onChange={e => { clearFieldError('decimalPlaces'); setForm(p => ({ ...p, decimalPlaces: e.target.value })); }}
              />
              {getFieldError('decimalPlaces') && <div className="form-error-message">{getFieldError('decimalPlaces')}</div>}
            </div>

            <div className="form-group" data-field="isoCode">
              <label className="form-label">ISO Code</label>
              <input
                type="text"
                className="form-input"
                value={form.isoCode}
                maxLength={10}
                placeholder="e.g. CS"
                onChange={e => setForm(p => ({ ...p, isoCode: e.target.value.toUpperCase() }))}
              />
              <div style={hintStyle}>UN/ECE Rec. 20, e.g. CS for case</div>
            </div>

            <div className="form-group" data-field="sortOrder">
              <label className="form-label">Sort Order</label>
              <input
                type="number"
                min={0}
                step={1}
                className={`form-input${getFieldError('sortOrder') ? ' form-field-error' : ''}`}
                value={form.sortOrder}
                onChange={e => { clearFieldError('sortOrder'); setForm(p => ({ ...p, sortOrder: e.target.value })); }}
              />
              {getFieldError('sortOrder') && <div className="form-error-message">{getFieldError('sortOrder')}</div>}
            </div>
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={form.description}
              placeholder="What this unit holds, e.g. a shrink-wrapped pack of 6"
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { void handleSave(); }} disabled={saving}>
            <IconSystem.check size={16} /> {mode === 'edit' ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`${mode === 'edit' ? 'Save changes to' : 'Create'} unit "${form.uomName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// VIEW MODAL
// ---------------------------------------------------------------------------

const ViewField: React.FC<{ label: string; value?: string | number | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px', whiteSpace: 'pre-wrap',
    }}>
      {value !== null && value !== undefined && value !== '' ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

interface UomViewModalProps {
  isOpen: boolean;
  record: UomDto | null;
  onClose: () => void;
  onEdit?: (r: UomDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (code: string) => void;
}

const UomViewModal: React.FC<UomViewModalProps> = ({
  isOpen, record, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !record) return;
    minimize({
      id: `${tabId}-view-modal-${record.uomCode}`,
      title: `View - ${record.uomCode}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(record.uomCode),
    });
    onClose();
  }, [tabId, moduleName, record, minimize, onClose, onRestoreView]);

  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Unit of Measure Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={record.uomCode} />
            <div>
              <label className="form-label">Type</label>
              <div style={{ paddingTop: '4px' }}>
                <span className="badge">{record.isSystem ? 'System' : 'User-defined'}</span>
              </div>
            </div>
            <ViewField label="Name" value={record.uomName} fullWidth />
            <ViewField label="Symbol" value={record.symbol} />
            <ViewField label="Category" value={categoryLabel(record.category)} />
            <ViewField label="Decimal Places" value={record.decimalPlaces} />
            <ViewField label="ISO Code" value={record.isoCode} />
            <ViewField label="Sort Order" value={record.sortOrder} />
            <ViewField label="In Use (items)" value={record.usageCount ?? 0} />
            <ViewField label="Description" value={record.description} fullWidth />
            {record.updatedAt && (
              <>
                <ViewField label="Updated By" value={record.updatedByName} />
                <ViewField label="Updated At" value={new Date(record.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(record); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// SCREEN
// ---------------------------------------------------------------------------

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'uomCode', label: 'Code' },
  { id: 'uomName', label: 'Name' },
  { id: 'symbol', label: 'Symbol' },
  { id: 'category', label: 'Category' },
  { id: 'decimalPlaces', label: 'Decimals' },
  { id: 'isoCode', label: 'ISO Code' },
  { id: 'usageCount', label: 'In Use' },
  { id: 'isSystem', label: 'System' },
  { id: 'actions', label: 'Actions', required: true },
];

interface UnitOfMeasureScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const UnitOfMeasureScreen: React.FC<UnitOfMeasureScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('unit-of-measure');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  // Search + category are server-side filters; the list itself is not paged (small).
  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState<string | undefined>(undefined);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchQuery.trim() || undefined), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);
  const [appliedCategory, setAppliedCategory] = useState<string | undefined>(undefined);
  const { data, loading, refetch } = useUoms(appliedCategory, search);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('unit-of-measure-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selected, setSelected] = useState<UomDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<UomDto | null>(null);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleEdit = useCallback((r: UomDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.uomCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((r: UomDto) => {
    const modalId = tabId ? `${tabId}-form-modal-copy-${r.uomCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleView = useCallback((r: UomDto) => {
    const viewId = tabId ? `${tabId}-view-modal-${r.uomCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setViewRecord(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((code: string) => {
    const rec = data.find(r => r.uomCode === code);
    if (rec) { setViewRecord(rec); setViewOpen(true); }
  }, [data]);

  const handleRestoreModal = useCallback(
    (m: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.uomCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleDelete = useCallback(async (r: UomDto) => {
    const ok = await confirmDelete({ resourceLabel: `${r.uomCode} - ${r.uomName}` });
    if (!ok) return;
    try {
      await uomService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Unit "${r.uomName}" deleted.`);
      refetch();
    } catch (err: unknown) {
      notificationService.toast.error(apiMessage(err, 'Could not delete the unit.'));
      if (apiStatus(err) === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<UomDto>[] = useMemo(() => [
    { id: 'uomCode', header: 'Code', accessorKey: 'uomCode', size: 120 },
    { id: 'uomName', header: 'Name', accessorKey: 'uomName', size: 200 },
    {
      id: 'symbol', header: 'Symbol', accessorKey: 'symbol', size: 100,
      cell: ({ row }) => (row.original as UomDto).symbol || '—',
    },
    {
      id: 'category', header: 'Category', accessorKey: 'category', size: 120,
      cell: ({ row }) => categoryLabel((row.original as UomDto).category),
    },
    { id: 'decimalPlaces', header: 'Decimals', accessorKey: 'decimalPlaces', size: 100, align: 'right' },
    {
      id: 'isoCode', header: 'ISO Code', accessorKey: 'isoCode', size: 100,
      cell: ({ row }) => (row.original as UomDto).isoCode || '—',
    },
    {
      id: 'usageCount', header: 'In Use', accessorKey: 'usageCount', size: 90, align: 'right',
      cell: ({ row }) => (row.original as UomDto).usageCount ?? 0,
    },
    {
      id: 'isSystem', header: 'System', accessorKey: 'isSystem', size: 100,
      cell: ({ row }) => ((row.original as UomDto).isSystem
        ? <span className="badge" title="Seeded unit: code fixed, cannot be deleted">System</span>
        : ''),
    },
  ], []);

  const rowActions = useCallback((r: UomDto): ActionMenuItem[] => {
    // Order: View → Copy → Edit → Delete. View is never gated.
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (can('CREATE')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
    }
    // System units and units in use by items cannot be deleted — omit the action.
    if (can('DELETE') && !r.isSystem && (r.usageCount ?? 0) === 0) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => { void handleDelete(r); }, variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Unit'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const appliedCount = appliedCategory ? 1 : 0;
  const HeaderIcon = moduleIcon ? ICONS[moduleIcon] : undefined;

  return (
    <EnterpriseScreenShell
      title="Units of Measure"
      description="Units items are stocked, bought and sold in — add pack units such as Shrink or Bundle here"
      icon={HeaderIcon ? <HeaderIcon size={18} /> : <IconSystem.grid size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search units...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Category"
                  options={CATEGORY_OPTIONS}
                  value={categoryFilter}
                  onChange={v => setCategoryFilter((v as string) || '')}
                  placeholder="All categories"
                  isClearable
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setCategoryFilter(''); setAppliedCategory(undefined);
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setAppliedCategory(categoryFilter || undefined);
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          // Not paged: the endpoint returns every unit (a small, bounded list).
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No units match — click Add.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {r.uomCode}{r.isSystem && <span className="badge" style={{ marginLeft: '0.4rem' }}>System</span>}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.uomName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {categoryLabel(r.category)} · {r.decimalPlaces} decimal{r.decimalPlaces === 1 ? '' : 's'} · used by {r.usageCount ?? 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <UomFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Units of Measure'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />

      <UomViewModal isOpen={viewOpen} record={viewRecord}
        onClose={() => { setViewOpen(false); setViewRecord(null); }}
        onEdit={can('UPDATE') ? handleEdit : undefined}
        tabId={tabId} moduleName={moduleName || 'Units of Measure'}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default UnitOfMeasureScreen;
